import { type CSSProperties, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import type {
  UndecoratedFile,
  UndecoratedFileVersion,
} from '@witmetrics/utils';
import {
  getFileCategory,
  isPdf,
  isScan,
  isSweethSequence,
  isViewableNonScan,
} from '@witmetrics/utils/files';
import { useDispatch, useFile } from '@witmetrics/data-store';
import { FILE_CATEGORY_COLORS } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { downloadFileVersion, ensurePreview, getFileName } from '@/utils/files';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import { showErrorToaster } from '@/utils/toasters';
import { loadScan } from '@/utils/scans';
import SceneProvider from '@/providers/SceneProvider';
import ViewerHeader, { FileControls } from '@/components/ViewerHeader';
import Card from '@/components/Card';
import FileCategoryIcon from '@/components/FileCategoryIcon';
import FileTimeline from './FileTimeline';
import ViewerContent from './ViewerContent';
import { classes, fetchFileData } from './utils';

export type FileViewerProps = {
  showTimeline?: boolean;
  className?: string;
  style?: CSSProperties;
  fileId: number;
  onToggleTimeline?: (open: boolean) => void;
  onClose?: () => void;
};

export default function FileViewer({
  showTimeline,
  className,
  style,
  fileId,
  onToggleTimeline,
  onClose,
}: FileViewerProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const file = useFile(fileId);
  const { loadedFiles, addLoadedFile } = useLoadedFiles();
  const [isFetchingData, toggleFetchingData] = useToggle(true);

  useEffect(() => {
    fetchData();
  }, [fileId]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      const data = await fetchFileData(fileId);
      dispatch.addFiles([data.file]);
      if (data.fileVersion) {
        dispatch.addFileVersions([data.fileVersion]);
        await handleOpenFileVersion(data.fileVersion, data.file.fileTypeId);
      }
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching file');
    }
  };

  const handleOpenFileVersion = async (
    fileVersion: UndecoratedFileVersion,
    fileTypeId?: UndecoratedFile['fileTypeId']
  ) => {
    try {
      if (!!loadedFiles[fileId]) return; // File already loaded
      const { id, name } = fileVersion;
      const arrayBuffer = await downloadFileVersion(id);
      if (isScan(name)) {
        const mesh = loadScan(name, arrayBuffer, fileTypeId);
        if (!mesh) {
          toggleFetchingData(false);
          return showErrorToaster('Unrecognized scan format');
        }
        addLoadedFile(fileId, mesh);
      } else if (isPdf(name)) {
        addLoadedFile(
          fileId,
          new Blob([arrayBuffer], { type: 'application/pdf' })
        );
      } else if (isSweethSequence(file!)) {
        addLoadedFile(fileId, new Blob([arrayBuffer]));
      } else if (isViewableNonScan(name)) {
        addLoadedFile(fileId, URL.createObjectURL(new Blob([arrayBuffer])));
      }
      toggleFetchingData(false);
      const preview = await ensurePreview(fileVersion, fileTypeId, arrayBuffer);
      if (preview) {
        dispatch.addFileVersions([preview.updatedVersion]);
        addLoadedFile(
          `preview-${fileVersion.id}`,
          URL.createObjectURL(preview.preview)
        );
      }
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error loading file');
    }
  };

  return (
    <div style={style} className={twMerge(classes.wrapper, className)}>
      <div className={classes.content}>
        {showTimeline && !isFetchingData && (
          <FileTimeline className={classes.timeline} fileId={fileId} />
        )}
        <Card className={classes.viewerCard}>
          {file && (
            <div className={classes.floatingHeader}>
              <ViewerHeader
                title={getFileName(file)}
                description={file?.description}
                colors={FILE_CATEGORY_COLORS[getFileCategory(file)]}
                icon={<FileCategoryIcon file={file} />}
                controls={
                  <FileControls
                    showTimeline={showTimeline}
                    fileId={fileId}
                    onToggleTimeline={onToggleTimeline}
                    onClose={onClose}
                  />
                }
              />
            </div>
          )}
          <SceneProvider>
            <ViewerContent isLoading={isFetchingData} fileId={fileId} />
          </SceneProvider>
        </Card>
      </div>
    </div>
  );
}
